import React, { useEffect, useState, useMemo } from 'react';
import Button from '../../Components/Button';
import { UniverseApi } from '../../API/UniverseApi';
import { Universe } from '../../Types/Universe';
import { StorylineDilemma } from '../../Types/StorylineDilemma';

interface UniverseModalProps {
  universe?: Universe;
  onStart?: () => void;
  onSuccess?: (universe: Universe, dilemmas?: StorylineDilemma[]) => void;
  onCancel?: () => void;
}

const UniverseModal: React.FC<UniverseModalProps> = ({
  universe,
  onStart,
  onSuccess,
  onCancel,
}) => {
  const [title, setTitle] = useState(universe?.title ?? '');
  const [description, setDescription] = useState(universe?.description ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const isInputValid = useMemo(() => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    return trimmedTitle.length > 0 && trimmedDescription.length > 0;
  }, [title, description]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Enter' &&
        (e.ctrlKey || e.metaKey) &&
        !isSubmitting &&
        isInputValid
      ) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSubmitting, isInputValid, title, description]);

  async function handleSubmit() {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle || !trimmedDescription) {
      setError('Please fill in both title and description');
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      onStart?.();

      const universeApi = new UniverseApi();
      const response = await universeApi.createUniverse(
        trimmedTitle,
        trimmedDescription,
      );
      onSuccess?.(response.universe, response.dilemmas);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError(err.response.data.error);
      } else {
        setError('Failed to create universe. Please try again.');
      }
      console.error('Error creating universe:', err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold mb-4">
        {universe ? 'Edit Universe' : 'Create Your Own Universe'}
      </h3>
      <div className="space-y-4 text-left">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            autoFocus
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            placeholder="Enter a title for your universe"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            required
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            rows={3}
            placeholder="Describe your universe"
          />
        </div>
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <div className="flex gap-2 pt-2 justify-end">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !isInputValid}
            isInProgress={isSubmitting}
            inProgressText={universe ? 'Saving...' : 'Creating...'}
          >
            {universe ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UniverseModal;
